import { connect } from 'react-redux';
import { createSelector, defaultMemoize } from 'reselect';
import ClassRoomPostsTable from './PostsTable';
import selectTagFilter from '../../../../../actions/navigation/selectTagFilter';
import { fetchClassRoomContent } from '../../../../../../core/actions/classRoom/content';
import filterByTag from '../../../../../../core/actions/classRoom/filter';
import { setNotificationAsReadByType } from '../../../../../../core/notifications/actions/';
import { currentUserId as getCurrentUserId } from '../../../../../../core/selectors/user';
import {
  openSettings,
  openImportantPosts,
} from '../../../../../actions/navigation';
import {
  isInstructor,
  currentClassRoomId,
  currentSpaceSelector,
  accessingCurrentSpaceAsStaff,
  importantPostsSelector,
  postsCountSelector,
  importantPostsCountSelector,
  recentImportantPostsSelector,
  scheduledPostCount,
  recentImportantPostsCountSelector,
  postsExcludingRecentImportantSelector,
} from '../../../../../../core/selectors/classroom';
import {
  selectors as notificationSelectors,
  operations as notificationOperations,
} from '../../../../../store/notifications';

const getCurrentUserIdSelector = defaultMemoize((state) =>
  getCurrentUserId(state)
);

const isEducatorSelector = createSelector(
  [(state) => state, getCurrentUserIdSelector],
  (state, userId) => isInstructor(state, userId)
);

const isSpaceMutedSelector = defaultMemoize((state) =>
  notificationSelectors.isSpaceMuted(state, currentClassRoomId(state))
);

const tagSelector = defaultMemoize((state) => state.data.tags);
const filterTagSelector = defaultMemoize((state) => state.classRoom.filterTag);
const isFetchingPostsSelector = defaultMemoize(
  (state) => state.posts.isFetchingPosts
);
const hasFetchedAllPostsSelector = defaultMemoize(
  (state) => state.posts.hasFetchedAllPosts
);

const selectedTagObject = createSelector(
  [tagSelector, filterTagSelector],
  (tags, filterTag) => tags[filterTag]
);

const getSelectedTag = (state) =>
  selectedTagObject(state) && selectedTagObject(state).objectId;

const mapStateToProps = (state) => {
  const currentSpaceState = currentSpaceSelector(state);
  const posts = postsExcludingRecentImportantSelector(state);
  const fetching = !posts.length && isFetchingPostsSelector(state);

  if (!currentSpaceState) {
    return {
      posts: [],
      tags: {},
      classRoom: undefined,
      fetching,
    };
  }

  return {
    currentUserId: getCurrentUserIdSelector(state),
    posts,
    isEducator: isEducatorSelector(state),
    selectedTag: getSelectedTag(state),
    tags: tagSelector(state),
    classRoom: currentSpaceState,
    fetching,
    hasFetchedAll: hasFetchedAllPostsSelector(state),
    spaceIsMuted: isSpaceMutedSelector(state),
    isReadOnlyUser: accessingCurrentSpaceAsStaff(state),
    postsCount: postsCountSelector(state),
    importantPosts: importantPostsSelector(state),
    importantPostsCount: importantPostsCountSelector(state),
    isChangingClassRoom: state.classRoom.isChangingClassRoom,
    recentImportantPosts: recentImportantPostsSelector(state),
    recentImportantPostsCount: recentImportantPostsCountSelector(state),
    scheduledPostCount: scheduledPostCount(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchNextPosts: ({ classRoom, tagId }) =>
    classRoom
      ? dispatch(
          fetchClassRoomContent({
            classRoom,
            reset: false,
            tagId,
          })
        )
      : Promise.resolve(),
  setRead: (classRoomId) =>
    new Promise((resolve) => {
      const p = dispatch(setNotificationAsReadByType('post', classRoomId));
      if (p && p.then) {
        p.then(() => {
          resolve();
        });
        return;
      }
      resolve();
    }),
  filterByTag: async (tagId, spaceId) => {
    await dispatch(filterByTag(tagId));
    dispatch(selectTagFilter(spaceId, tagId));
  },
  toggleMuteSpace: (spaceId) =>
    dispatch(notificationOperations.toggleMuteSpace(spaceId)),
  openSettings: (spaceId) => dispatch(openSettings(spaceId)),
  openImportantPosts: () => dispatch(openImportantPosts()),
});

const ClassRoomPostsTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(ClassRoomPostsTable);

export default ClassRoomPostsTableContainer;
